import {
  Box,
  Button,
  Flex,
  Heading,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  ModalCloseButton,
} from '@chakra-ui/react';
import { compositionGroupMatrixApi } from '@texas/api/endpoints/compositionGroup/compositionGroupMatrixApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useVersionOverviewContext } from '../shared/VersionOverviewContext';
import { compositionVersionEvents } from '../events';
import { CompositionGroupView } from '../../compositions/shared/group/CompositionGroupView';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { CompositionGroupTitle } from '@texas/components/shared/compositionGroup/CompositionGroupTitle';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { CompositionLocationData } from '../../compositions/shared/hooks/useCompositionGroup';
import { ArchivedCompositionGroupsModal } from '../../compositions/shared/group/ArchivedCompositionGroupsModal';
import { MatchMode } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { variantsApi } from '@texas/api/endpoints/variantsApi';
import { ControllerVersionsList } from './ControllerVersionsList';

export function ControlledOptions({ variantId }: { variantId: number }) {
  const { t } = useTranslation();
  const { filters } = useVersionOverviewContext();
  const { value, isOpen, onClose, onOpen } = useValueDisclosure<number>();
  const {
    value: archiveValue,
    isOpen: isArchiveOpen,
    onClose: onArchiveClose,
    onOpen: onArchiveOpen,
  } = useValueDisclosure<CompositionLocationData>();

  const { data: variantSuppliersOverview, refetch: refetchSuppliers } =
    useApiResource(variantsApi.getVariantBranchSuppliers);

  const { data, refetch, loading } = useApiResource(
    compositionGroupMatrixApi.getOptions,
  );

  const {
    request: addControllerVersionRequest,
    loading: isAddingControllerVersion,
  } = useApiRequest(compositionGroupMatrixApi.addControllerVersion);

  useEffect(() => {
    refetch(variantId);
    refetchSuppliers(variantId, false);
  }, [refetch, refetchSuppliers, variantId]);

  const filteredOptions = useMemo(() => {
    if (!data) return [];
    if (!filters.optionIds?.length) return data;
    return data.filter((x) => filters.optionIds!.includes(x.id));
  }, [data, filters.optionIds]);

  const addControllerVersion = async (
    supplierId: number,
    branchId: number,
    controllerId: number,
  ) => {
    await request(
      addControllerVersionRequest,
      [controllerId, branchId, supplierId],
      (version) => {
        compositionVersionEvents.onVersionsPatched.dispatch();
        compositionVersionEvents.onControllerVersionCreated.dispatch(version);
      },
    );
  };

  return (
    <Box>
      <Heading fontSize="md" pb={2}>
        {t('general.options')}
      </Heading>
      {loading && (
        <SpinnerWithLabel label={t('compositionGroup.option.loading')} />
      )}
      <Flex gap={3} wrap="wrap">
        {filteredOptions.map((x) => {
          return (
            <Flex
              direction="column"
              key={x.id}
              alignItems="center"
              w="fit-content"
              maxW={72}
            >
              <Menu isLazy={true}>
                <MenuButton
                  as={Button}
                  isLoading={isAddingControllerVersion}
                  rightIcon={<Icons.ChevronDown />}
                  h="auto"
                  w="100%"
                  textAlign="start"
                  isDisabled={!x.isReady}
                  variant="texas-light"
                  whiteSpace="normal"
                >
                  <Flex p={2} flexDir="column" gap={1}>
                    <CompositionGroupTitle
                      version={x.version}
                      note={x.note}
                      type="option"
                      fontSize="md"
                      linkedVariantId={x.linkedVariantId}
                    />

                    {!x.isReady && (
                      <Text
                        fontSize="sm"
                        fontWeight="normal"
                        color="yellow.400"
                        _light={{ color: 'orange.500' }}
                      >
                        {t('compositionGroup.option.notYetReady')}
                      </Text>
                    )}
                  </Flex>
                </MenuButton>
                <MenuList zIndex={3}>
                  <MenuItem
                    icon={<Icons.OpenInNew boxSize={defaultIconSize} />}
                    onClick={() => onOpen(x.id)}
                  >
                    {t('compositionGroup.option.preview')}
                  </MenuItem>
                  {variantSuppliersOverview &&
                    variantSuppliersOverview.suppliers.length > 0 &&
                    (() => {
                      const filteredSuppliers =
                        variantSuppliersOverview.suppliers.filter(
                          (s) =>
                            !s.disabled &&
                            (!filters.supplierIds?.length ||
                              filters.supplierIds.includes(s.supplierId)) &&
                            (!filters.branchIds?.length ||
                              filters.branchIds.includes(s.branchId)),
                        );
                      if (!filteredSuppliers.length) return null;
                      return (
                        <>
                          <MenuDivider />
                          {variantSuppliersOverview.branches.map((branch) => {
                            const branchSuppliers = filteredSuppliers.filter(
                              (s) => s.branchId === branch.id,
                            );
                            if (!branchSuppliers.length) return null;
                            return (
                              <MenuGroup
                                key={branch.id}
                                title={branch.identifier}
                              >
                                {branchSuppliers.map((s) => (
                                  <MenuItem
                                    key={s.id}
                                    icon={
                                      <Icons.Plus boxSize={defaultIconSize} />
                                    }
                                    onClick={async () =>
                                      await addControllerVersion(
                                        s.supplierId,
                                        s.branchId,
                                        x.id,
                                      )
                                    }
                                  >
                                    {s.supplierName}
                                  </MenuItem>
                                ))}
                              </MenuGroup>
                            );
                          })}
                        </>
                      );
                    })()}
                  <MenuDivider />
                  <MenuItem
                    icon={<Icons.ArchiveOutline boxSize={defaultIconSize} />}
                    onClick={() =>
                      onArchiveOpen({
                        variantId,
                        controllerId: x.id,
                      })
                    }
                  >
                    {t('compositionGroup.version.showArchivedVersions')}
                  </MenuItem>
                </MenuList>
              </Menu>
              <ControllerVersionsList
                optionId={x.id}
                isReady={x.isReady}
                supplierIds={filters.supplierIds}
                branchIds={filters.branchIds}
              />
            </Flex>
          );
        })}
      </Flex>
      {archiveValue && (
        <ArchivedCompositionGroupsModal
          locationData={archiveValue}
          isOpen={isArchiveOpen}
          onClose={onArchiveClose}
          matchMode={MatchMode.Fuzzy}
        />
      )}
      <Modal isCentered={true} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent width="auto" maxW="fit-content">
          <ModalCloseButton />
          <ModalBody>{value && <InnerModal id={value} />}</ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function InnerModal({ id }: { id: number }) {
  return (
    <>
      <CompositionGroupView
        variantId={0}
        compositionGroupId={id}
        componentSettings={{
          expandedDefault: true,
          addMaterials: 'hidden',
          materialsMode: 'read-only',
          removeMaterials: 'hidden',
          editImage: 'hidden',
          dimensionMode: 'read-only-group',
          compositionArchiveRestore: 'hidden',
          colors: 'hidden',
        }}
      />
    </>
  );
}
